import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Info, Loader2, TriangleAlert } from 'lucide-react';
import { MembershipOfferBlock } from '@/components/organisms/estimates/MembershipOfferBlock';
import type { MembershipOffer } from '@/hooks/estimates/useMembershipOfferPreview';
import { Button } from '@/components/atoms/button';
import { Label } from '@/components/atoms/label';
import { useNumberField } from '@/components/molecules/NumberInput';
import { Switch } from '@/components/atoms/switch';
import { Textarea } from '@/components/atoms/textarea';
import { Badge } from '@/components/atoms/badge';
import { usePromotions } from '@/hooks/operations/usePromotions';
import type { Promotion } from '@/types/promotion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/atoms/tooltip';
import {
  formatTaxRate,
  taxBreakdownDisplayLabel,
  taxBreakdownDisplayMeta,
  taxBreakdownDisplayName,
  type AggregatedTaxBreakdownRow,
} from '@/lib/tax/tax-utils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TaxResolution {
  group?: { name: string; combinedRate: number } | null;
}

interface MembershipInfo {
  planName: string | null;
}

type PromotionDiscountPatch = {
  discountType: '$' | '%';
  discountValue: number;
  discountReason: string;
};

export interface FinancialsSectionProps {
  // Discount
  discountValue: number;
  discountType?: '$' | '%';
  discountReason: string;
  onDiscountValueChange: (v: number) => void;
  onDiscountTypeChange: (t?: '$' | '%') => void;
  onDiscountReasonChange: (r: string) => void;
  promotionId?: string | null;
  showPromotionSelector?: boolean;
  onPromotionChange?: (promotionId: string | null, patch?: PromotionDiscountPatch) => void;

  // Deposit
  depositValue: number;
  depositType: '$' | '%';
  depositRequired: boolean;
  onDepositValueChange: (v: number) => void;
  onDepositTypeChange: (t: '$' | '%') => void;
  onDepositRequiredChange: (v: boolean) => void;

  // Computed totals (parent is responsible for calculation)
  subtotal: number;
  discountAmount: number;
  depositAmount: number;
  grandTotal: number;
  /**
   * The base a flat deposit is checked against (grand total minus any
   * membership line — what the server caps `depositAmount` at). Falls back to
   * `grandTotal` for callers that have no membership exclusion to make.
   */
  depositEligibleTotal?: number;
  /**
   * Enforce the deposit upper bound (100% / the deposit-eligible total) with
   * an inline error. The ESTIMATE forms pass this — their server refuses such
   * a deposit. The job form does not: a flat job deposit is a fixed dollar
   * commitment its server deliberately never clamps, so the message would be
   * false there.
   */
  enforceDepositBound?: boolean;
  /**
   * Is the deposit being SET by this save? The bound is judged only then —
   * the server keeps an estimate storing an oversized deposit editable — so
   * the inline error and the Save gate say the same thing. Defaults to true.
   */
  depositChanged?: boolean;
  // Optional relabeling of the grand-total row. Purely presentational — callers
  // (e.g. the recurring New Job form) qualify what the total means per billing
  // mode. Defaults keep estimates/proposals/invoices visually unchanged.
  grandTotalLabel?: string;
  grandTotalCaption?: ReactNode;

  // T&M override (optional)
  showTM?: boolean;
  pricingMode?: 'STANDARD' | 'TM_OVERRIDE';
  onPricingModeChange?: (mode: 'STANDARD' | 'TM_OVERRIDE') => void;
  /**
   * Services this T&M document is quoting WITHOUT their parts, by name — the
   * shape a document saved before the switch materialized anything still holds
   * (`tmOverrideUnderquotedServices`). Empty, or absent, draws nothing.
   */
  tmUnderquotedServices?: string[];
  /**
   * Reprice those services from their parts. The operator's click is the whole
   * point: the quoted number is PERSISTED and the customer may be holding it, so
   * nothing here may move it on its own.
   */
  onTMRepriceFromParts?: () => void;

  // Tax (optional — estimates only)
  taxTotal?: number;
  taxLoading?: boolean;
  taxResolution?: TaxResolution | null;
  taxAddressProvided?: boolean;
  /** Tax Policy layer: the rule frozen into the document's taxSummary. */
  taxPolicy?: {
    id: string;
    name: string;
    taxableBases: string[];
  } | null;
  /** When set, renders a "change" affordance next to the tax line (MANUAL writer). */
  onChangeTaxGroup?: () => void;
  // Per-jurisdiction breakdown (optional). When present, rows replace the single
  // blended Tax line — parity with the detail's FinancialSummaryBlock. Structurally
  // compatible with AggregatedTaxBreakdownRow.
  taxBreakdown?: AggregatedTaxBreakdownRow[];

  // Membership savings (optional — estimates only)
  membershipDiscountPreview?: number;
  membershipInfo?: MembershipInfo | null;
  // NOT-605 — "join and save today" offer for a non-member (estimates only).
  membershipOffer?: MembershipOffer | null;

  // Visibility controls for flows that reuse the same financial grammar
  showDeposit?: boolean;
  showTax?: boolean;
  /** Hide the compact totals when the caller renders a richer summary. */
  showSummary?: boolean;
  /** An extra line under the grand total (the job form puts margin here). */
  summaryExtra?: ReactNode;
  /**
   * `split` puts the breakdown in a column beside the controls; `stacked` (the
   * default, and what estimates and OptionCard use) keeps it underneath.
   */
  layout?: 'stacked' | 'split';
  allowEmptyDiscountType?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function promotionDiscountType(promotion: Promotion): '$' | '%' {
  return promotion.discountType === 'percent' ? '%' : '$';
}

function promotionLabel(promotion: Promotion): string {
  const suffix =
    promotion.discountType === 'percent'
      ? `${promotion.discountValue}%`
      : fmt(promotion.discountValue);
  return `${promotion.name} · ${suffix}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function FinancialsSection({
  discountValue,
  discountType,
  discountReason,
  onDiscountValueChange,
  onDiscountTypeChange,
  onDiscountReasonChange,
  promotionId,
  showPromotionSelector = false,
  onPromotionChange,
  depositValue,
  depositType,
  depositRequired,
  onDepositValueChange,
  onDepositTypeChange,
  onDepositRequiredChange,
  subtotal,
  discountAmount,
  depositAmount,
  grandTotal,
  depositEligibleTotal = grandTotal,
  enforceDepositBound = false,
  depositChanged = true,
  grandTotalLabel = 'Grand Total',
  grandTotalCaption,
  showTM = false,
  pricingMode = 'STANDARD',
  onPricingModeChange,
  tmUnderquotedServices,
  onTMRepriceFromParts,
  taxTotal = 0,
  taxLoading = false,
  taxResolution,
  taxPolicy,
  onChangeTaxGroup,
  taxAddressProvided = false,
  taxBreakdown,
  membershipDiscountPreview = 0,
  membershipInfo,
  membershipOffer,
  showDeposit = true,
  showTax = true,
  showSummary = true,
  layout = 'stacked',
  summaryExtra,
  allowEmptyDiscountType = false,
}: FinancialsSectionProps) {
  const promotionsQuery = usePromotions({ status: 'active' }, { enabled: showPromotionSelector });
  const promotions = showPromotionSelector ? (promotionsQuery.data ?? []) : [];

  const clearPromotionAttribution = () => {
    if (promotionId) onPromotionChange?.(null);
  };

  const handlePromotionChange = (nextPromotionId: string) => {
    if (nextPromotionId === '__none__') {
      onPromotionChange?.(null);
      return;
    }

    const promotion = promotions.find((entry) => entry.id === nextPromotionId);
    if (!promotion) return;
    const replacingExistingDiscount =
      promotionId !== promotion.id && Boolean(discountType) && discountValue > 0;
    if (
      replacingExistingDiscount &&
      !window.confirm('Applying this promotion will replace the current document discount.')
    ) {
      return;
    }
    onPromotionChange?.(promotion.id, {
      discountType: promotionDiscountType(promotion),
      discountValue: promotion.discountValue,
      discountReason: `Promo: ${promotion.name}`,
    });
  };

  // NOT-242: a discount can't exceed 100% / the subtotal (strict `>`, so an
  // exact-subtotal discount → $0 total is allowed). Derived from the live
  // subtotal, so it re-validates when line items change.
  const discountInvalid = !discountType
    ? false
    : discountType === '%'
      ? discountValue > 100
      : discountValue > subtotal;

  // Both amounts read as 0 when the box is empty, but the box stays empty so a
  // new figure can be typed over the old one instead of fighting a leading 0.
  const discountField = useNumberField({
    value: discountType ? discountValue || null : null,
    emptyValue: 0,
    onChange: (next) => {
      clearPromotionAttribution();
      const raw = next ?? 0;
      onDiscountValueChange(discountType === '%' ? Math.min(100, raw) : raw);
    },
  });

  // Same shape as the discount: the server caps `depositAmount` at the total
  // but stores `depositValue` as typed, so an oversized deposit would leave the
  // builder and the PDF disagreeing for good. Strict `>`, so a 100% / whole-
  // total deposit is allowed. Derived from the live total, so it re-validates
  // when line items change.
  // Judged whether or not the deposit is "Required to approve": that switch
  // only decides WHEN the customer pays, and an optional deposit is quoted and
  // charged all the same (NOT-716). Only the estimate forms hold their
  // server's bound, so only they show it.
  const depositInvalid =
    enforceDepositBound &&
    depositChanged &&
    (depositType === '%' ? depositValue > 100 : depositValue > depositEligibleTotal);

  const depositField = useNumberField({
    value: depositValue || null,
    emptyValue: 0,
    onChange: (next) => {
      const raw = next ?? 0;
      const v = depositType === '%' ? Math.min(100, raw) : raw;
      onDepositValueChange(v);
      // A required deposit with no amount is invalid (backend rejects it), so
      // clear the flag when the amount drops to 0. (NOT-244)
      if (v <= 0 && depositRequired) onDepositRequiredChange(false);
    },
  });

  // Split mode sizes the money fields to what goes in them. Stretched to fill
  // the column they ran right up against the breakdown, and a discount box a
  // third of a metre wide claims far more than "0".
  const splitField = layout === 'split' ? 'max-w-[14rem]' : '';

  const controls = (
    <>
      {showPromotionSelector && onPromotionChange ? (
        <div className="min-w-0 space-y-1.5">
          <Label className="text-sm font-medium">Promotion</Label>
          <Select
            value={promotionId ?? '__none__'}
            onValueChange={handlePromotionChange}
            disabled={promotionsQuery.isLoading}
          >
            <SelectTrigger className={cn('h-9 w-full min-w-0 text-sm', splitField)}>
              <SelectValue
                placeholder={promotionsQuery.isLoading ? 'Loading promotions' : 'No promotion'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No promotion</SelectItem>
              {promotions.map((promotion) => (
                <SelectItem key={promotion.id} value={promotion.id}>
                  {promotionLabel(promotion)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {/* Fixed tracks, not halves. Split in two, these two 224px money fields
          sat at opposite ends of an 800px column with 350px of nothing between
          the number and the number beside it. */}
      <div
        className={cn(
          'grid gap-x-6 gap-y-4',
          showDeposit ? 'grid-cols-1 sm:grid-cols-[repeat(auto-fill,14rem)]' : 'grid-cols-1',
        )}
      >
        {/* Discount */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Discount</Label>
          <div
            className={cn(
              'flex min-w-0 items-center rounded-md border border-input bg-background focus-within:ring-1 focus-within:ring-ring',
              splitField,
            )}
          >
            <input
              min={0}
              max={discountType === '%' ? 100 : undefined}
              step={discountType === '%' ? 1 : 0.01}
              placeholder="0"
              {...discountField}
              className="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm focus:outline-none"
            />
            <div className="shrink-0 border-l border-input">
              <Select
                value={discountType ?? '__none__'}
                onValueChange={(v) => {
                  clearPromotionAttribution();
                  onDiscountTypeChange(
                    v === '__none__' ? undefined : (v as FinancialsSectionProps['discountType']),
                  );
                }}
              >
                <SelectTrigger className="h-9 w-[4.25rem] min-w-0 border-0 bg-transparent px-2.5 text-sm shadow-none focus:ring-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allowEmptyDiscountType && <SelectItem value="__none__">No discount</SelectItem>}
                  <SelectItem value="$">$</SelectItem>
                  <SelectItem value="%">%</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {discountInvalid ? (
            <p className="text-xs text-destructive">
              {discountType === '%'
                ? 'Discount can’t exceed 100%.'
                : 'Discount can’t exceed the subtotal.'}
            </p>
          ) : null}
          {discountType && discountValue > 0 && (
            <Textarea
              placeholder="Reason for discount (optional)"
              rows={2}
              value={discountReason}
              onChange={(e) => {
                clearPromotionAttribution();
                onDiscountReasonChange(e.target.value);
              }}
            />
          )}
        </div>

        {showDeposit && (
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Deposit</Label>
            <div
              className={cn(
                'flex min-w-0 items-center rounded-md border border-input bg-background focus-within:ring-1 focus-within:ring-ring',
                splitField,
              )}
            >
              <input
                min={0}
                max={depositType === '%' ? 100 : undefined}
                step={depositType === '%' ? 1 : 0.01}
                placeholder="0"
                {...depositField}
                className="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm focus:outline-none"
              />
              <div className="shrink-0 border-l border-input">
                <Select
                  value={depositType}
                  onValueChange={(v) => {
                    const next = v as '$' | '%';
                    onDepositTypeChange(next);
                    // The same clamp typing gets: a $250 flat deposit switched
                    // to percent is not a 250% deposit.
                    if (next === '%' && depositValue > 100) onDepositValueChange(100);
                  }}
                >
                  <SelectTrigger className="h-9 w-[4.25rem] min-w-0 border-0 bg-transparent px-2.5 text-sm shadow-none focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="$">$</SelectItem>
                    <SelectItem value="%">%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {depositInvalid ? (
              <p className="text-xs text-destructive">
                {depositType === '%'
                  ? 'Deposit can’t exceed 100%.'
                  : 'Deposit can’t exceed the total.'}
              </p>
            ) : null}
            {/* Also shown at a 0 amount when the flag is already on — otherwise a
                stored "required deposit, no amount" estimate has no way to turn
                it back off, and the save gate blocks with no escape. */}
            {(depositValue > 0 || depositRequired) && (
              <div className="space-y-1">
                <div className="flex items-center gap-1.5">
                  <Switch
                    id="deposit-required"
                    checked={depositRequired}
                    onCheckedChange={onDepositRequiredChange}
                  />
                  {/* This switch is a COLLECTION POLICY, not "is there a
                      deposit" — the amount above is that. On: the customer
                      pays the deposit BEFORE the document can be approved
                      (estimate quote and proposal option alike — the option
                      is paid for on the estimate born from it). Off: the
                      deposit is quoted and collected after approval. */}
                  <Label
                    htmlFor="deposit-required"
                    className="cursor-pointer text-xs text-muted-foreground"
                  >
                    Required to approve
                  </Label>
                </div>
                {/* The state the old label hid. A deposit with the switch off
                    is still quoted on the document and still charged by the
                    engine — it simply is not a precondition for the work being
                    scheduled. Only in that state: with no amount there is no
                    deposit to describe, and with the switch on the label
                    already says it. */}
                {depositValue > 0 && !depositRequired ? (
                  <p className="text-xs text-muted-foreground">
                    Quoted on the document; approval does not wait for it.
                  </p>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
  /*
   * The breakdown. `layout="split"` sits it in a column beside the controls
   * instead of stacked under them — the shape an invoice has had for a century,
   * because the numbers are a result to read, not another thing to fill in.
   * Stacked, a bordered grey slab under the fields read as one more input
   * group. The default is unchanged, so the estimate builder and OptionCard
   * are untouched.
   */
  const summary = (
    /* Split mode drops the grey slab: beside the controls the breakdown is
       already its own column, and a filled, bordered box there read as one
       more input group rather than as the result of the ones next to it. The
       Grand Total's rule carries the emphasis instead. Stacked keeps the card,
       where it IS the only thing separating the totals from the fields above. */
    <div
      className={cn(
        'text-sm',
        // Beside the controls there is no box holding these lines together, so
        // 6px of air between them ran the whole breakdown into one block of
        // text. The stacked card keeps its tighter rhythm — its border already
        // does the separating.
        layout === 'split'
          ? 'space-y-2.5 px-0.5'
          : 'space-y-1.5 rounded-md border bg-muted/30 px-4 py-3',
      )}
    >
      {showTM && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <span>T&amp;M override</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
                    aria-label="Time and materials override details"
                  >
                    <Info className="h-3 w-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  Forces SERVICE items to use Time &amp; Materials pricing.
                </TooltipContent>
              </Tooltip>
            </span>
            <Switch
              checked={pricingMode === 'TM_OVERRIDE'}
              onCheckedChange={(v) => onPricingModeChange?.(v ? 'TM_OVERRIDE' : 'STANDARD')}
            />
          </div>
          {/* SAVED BEFORE THE SWITCH MATERIALIZED ANYTHING.

                Turning T&M on now builds each service's price from its labor and
                its parts; a document stored before that carries services the
                mode prices from the service line alone, so their parts charge
                nothing and the quote is short by exactly what the parts are
                worth.

                Repairing it is NOT done for them. The quoted number is persisted
                — the public quote page and the PDF read the stored figures, and
                an approved estimate's is countersigned into an immutable PDF —
                so this is the price the customer may be holding, and moving it
                is a revision, not a rendering. The button below is the only
                thing that moves it, and it runs the same `applyPricingMode` the
                switch does. */}
          {pricingMode === 'TM_OVERRIDE' &&
          onTMRepriceFromParts &&
          tmUnderquotedServices &&
          tmUnderquotedServices.length > 0 ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
              <p className="flex items-start gap-1.5">
                <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>
                  <span className="font-medium">
                    {tmUnderquotedServices.length === 1
                      ? '1 service is priced without its parts'
                      : `${tmUnderquotedServices.length} services are priced without their parts`}
                  </span>{' '}
                  ({tmUnderquotedServices.join(', ')}). This document was saved before time &amp;
                  materials priced services from their parts, so it is quoting less than the work
                  comes to. Repricing changes the total — if the customer already has this quote,
                  send them a revision.
                </span>
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-2 h-7 border-amber-300 bg-white text-xs text-amber-900 hover:bg-amber-100 dark:border-amber-500/40 dark:bg-transparent dark:text-amber-200"
                onClick={onTMRepriceFromParts}
              >
                Reprice from parts
              </Button>
            </div>
          ) : null}
        </div>
      )}
      <div className="flex justify-between">
        <span className="text-muted-foreground">Subtotal</span>
        <span>{fmt(subtotal)}</span>
      </div>
      {discountAmount > 0 && (
        <div className="flex justify-between">
          <span className="text-muted-foreground">Discount</span>
          <span className="text-destructive">-{fmt(discountAmount)}</span>
        </div>
      )}
      {membershipDiscountPreview > 0 && (
        <div className="flex justify-between text-emerald-600">
          <span className="flex items-center gap-1.5">
            <span>Membership savings</span>
            {membershipInfo?.planName && (
              <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium">
                {membershipInfo.planName}
              </span>
            )}
          </span>
          <span>-{fmt(membershipDiscountPreview)}</span>
        </div>
      )}
      {showTax &&
        (taxLoading ? (
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              Tax
              <Loader2 className="h-3 w-3 animate-spin" />
            </span>
            <span>{fmt(taxTotal)}</span>
          </div>
        ) : taxBreakdown && taxBreakdown.length > 0 ? (
          // Per-jurisdiction rows (parity with the detail) — replace the
          // blended line. The rule chip and the change-tax-group action
          // render on THIS branch too: a taxed document is the common
          // path, and hiding the pin control there made it unreachable.
          <>
            {taxPolicy || onChangeTaxGroup ? (
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  Tax
                  {taxPolicy ? (
                    <Badge
                      variant="outline"
                      className="max-w-[9rem] border-transparent bg-indigo-100 text-[10px] text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300"
                      title={
                        taxPolicy.taxableBases.includes('all')
                          ? `Tax rule applied: ${taxPolicy.name}`
                          : `Tax rule applied: ${taxPolicy.name} — only ${taxPolicy.taxableBases.join(' + ') || 'nothing'} taxable`
                      }
                    >
                      <span className="truncate">{taxPolicy.name}</span>
                    </Badge>
                  ) : null}
                  {onChangeTaxGroup ? (
                    <button
                      type="button"
                      className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                      title="Pin this document to a specific tax group"
                      onClick={onChangeTaxGroup}
                    >
                      change
                    </button>
                  ) : null}
                </span>
                <span />
              </div>
            ) : null}
            {taxBreakdown.map((row, i) => (
              <div
                key={`${row.primary}-${row.secondary ?? ''}-${row.jurisdictionType ?? ''}-${row.rate}-${i}`}
                className="flex items-start justify-between"
              >
                {/* A real jurisdiction name — "GST (Federal) — British Columbia
                    Provincial Sales Tax" — is wider than this 288px column.
                    Clipped to one line it stays a table; the whole name is on
                    the row's tooltip, so nothing is lost by clipping it. */}
                <span
                  className="flex min-w-0 flex-col pr-3 text-muted-foreground"
                  title={taxBreakdownDisplayLabel(row)}
                >
                  <span className="truncate">{taxBreakdownDisplayName(row)}</span>
                  <span className="truncate text-xs text-muted-foreground/70">
                    {taxBreakdownDisplayMeta(row)}
                  </span>
                </span>
                <span className="shrink-0">{fmt(row.amount)}</span>
              </div>
            ))}
          </>
        ) : (
          // Fallback: single blended line (single-code group / unresolved).
          <div className="flex items-center justify-between">
            <span className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
              <span className="shrink-0">Tax</span>
              {taxResolution?.group ? (
                // The group name is tenant-authored and can run long; it gives
                // up its width to the amount rather than pushing it out of the
                // row, and says the whole thing on hover.
                <span className="min-w-0">
                  <Badge
                    variant="secondary"
                    className="max-w-full text-xs font-normal"
                    title={`${taxResolution.group.name} · ${formatTaxRate(taxResolution.group.combinedRate)}`}
                  >
                    <span className="truncate">
                      {taxResolution.group.name} · {formatTaxRate(taxResolution.group.combinedRate)}
                    </span>
                  </Badge>
                </span>
              ) : (
                <span className="text-xs italic">
                  {taxAddressProvided ? 'No tax rule configured' : 'Enter ZIP to calculate'}
                </span>
              )}
              {taxPolicy ? (
                <Badge
                  variant="outline"
                  className="max-w-[9rem] border-transparent bg-indigo-100 text-[10px] text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300"
                  title={
                    taxPolicy.taxableBases.includes('all')
                      ? `Tax rule applied: ${taxPolicy.name}`
                      : `Tax rule applied: ${taxPolicy.name} — only ${taxPolicy.taxableBases.join(' + ') || 'nothing'} taxable`
                  }
                >
                  <span className="truncate">{taxPolicy.name}</span>
                </Badge>
              ) : null}
              {onChangeTaxGroup ? (
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                  title="Pin this document to a specific tax group"
                  onClick={onChangeTaxGroup}
                >
                  change
                </button>
              ) : null}
            </span>
            <span>{fmt(taxTotal)}</span>
          </div>
        ))}
      <div
        className={cn(
          'flex justify-between border-t font-semibold',
          layout === 'split' ? 'mt-1 border-border/60 pt-3 text-[17px]' : 'pt-1.5 text-base',
        )}
      >
        <span>{grandTotalLabel}</span>
        <span>{fmt(grandTotal)}</span>
      </div>
      {grandTotalCaption ? (
        // Belongs to the total above it, not to the deposit line under it.
        <p className="!mt-1 text-xs font-normal leading-5 text-muted-foreground">
          {grandTotalCaption}
        </p>
      ) : null}
      {showDeposit && depositAmount > 0 && (
        <div className="flex justify-between pt-0.5">
          <span className="text-muted-foreground">Deposit Due</span>
          <span className="font-medium">{fmt(depositAmount)}</span>
        </div>
      )}
      {summaryExtra}
    </div>
  );

  if (layout === 'split') {
    return (
      <div className="grid grid-cols-1 gap-x-12 gap-y-5 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start">
        <div className="min-w-0 space-y-4">{controls}</div>
        <div className="min-w-0">
          {showSummary ? summary : null}
          {membershipOffer ? (
            <MembershipOfferBlock offer={membershipOffer} className="mt-2" />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {controls}
      {showSummary ? summary : null}
      {membershipOffer ? <MembershipOfferBlock offer={membershipOffer} className="mt-2" /> : null}
    </div>
  );
}
